import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import "./css/AlertDisplay.css";
import {apiFetch, apiFetchJson} from "apiClient";
import {deleteDownloadedContractNote} from "@portfolio/utils/PFService";

const AlertDisplay = ({ isOpen, onClose, onImport, refreshVersion = 0 }) => {
    const [loading, setLoading] = useState(false);
    const [notes, setNotes] = useState([]);
    const [error, setError] = useState(null);

    const fetchPendingContractNotes = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await apiFetchJson("/api/portfolio/pending");
            setNotes(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch pending contract notes", e);
            setError("Failed to load pending contract notes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchPendingContractNotes();
        }
    }, [isOpen, refreshVersion]);


    if (!isOpen) return null;

    const handleImportClick = (note) => {
        if (onImport) {
            onImport(note);
        } else {
            // Placeholder so you can wire later
            console.log("Import clicked for note:", note);
        }
    };

    const handleDeleteDownloaded = async (id) => {
        if (!window.confirm("Are you sure you want to delete this downloaded Contract Note?")) return;

        try {
            await deleteDownloadedContractNote(id);
            await fetchPendingContractNotes(); // just refresh locally
        } catch (error) {
            console.error("Error deleting downloaded contract note:", error);
            setError(error.message || "Failed to delete contract note");
        }
    };

    const formatDate = (value) => {
        if (!value) return "";
        try {
            // LocalDate from backend -> ISO string
            const d = new Date(value);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                });
            }
        } catch (e) {
            // ignore
        }
        return String(value);
    };


    return (
        <div className="cn-alert-overlay">
            <div className="cn-alert-dialog">
                <div className="cn-alert-header">
                    <div className="cn-alert-title">
                        Pending Contract Notes to Import
                    </div>
                    <button
                        className="cn-alert-close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="cn-alert-body">
                    {loading && (
                        <div className="cn-alert-loading">
                            <div className="cn-alert-spinner" />
                            <span>Loading pending contract notes…</span>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="cn-alert-error">
                            {error}
                        </div>
                    )}

                    {!loading && !error && notes.length === 0 && (
                        <div className="cn-alert-empty">
                            No pending contract notes to import.
                        </div>
                    )}

                    {!loading && !error && notes.length > 0 && (
                        <div className="cn-alert-table-wrapper">
                            <table className="cn-alert-table">
                                <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Zerodha A/C</th>
                                    <th>File</th>
                                    <th>Email Subject</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {notes.map(note => (
                                    <tr key={note.id}>
                                        <td>{formatDate(note.contractNoteDate)}</td>
                                        <td>{note.zerodhaAccount}</td>
                                        <td>
                                            <div className="cn-file-cell">
                                                <span className="cn-file-name">
                                                    {note.fileName || "(unknown)"}
                                                </span>
                                                <div className="cn-file-path" title={note.filePath}>
                                                    {note.filePath}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="cn-email-subject">
                                            {note.emailSubject}
                                        </td>
                                        <td>{note.importStatus}</td>
                                        <td>
                                            <button
                                                className="cn-import-btn"
                                                onClick={() => handleImportClick(note)}
                                            >
                                                Import
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                className="cn-delete-btn"
                                                onClick={() => handleDeleteDownloaded(note.id)}
                                            >
                                                ✕
                                            </button>
                                        </td>

                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="cn-alert-footer">
                    <button className="cn-alert-secondary-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AlertDisplay;
